import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from './SEO';
import { Mountain, Waves, TreePine, Sun, Compass } from 'lucide-react';

const natureHero = "https://images.unsplash.com/photo-1506461883276-594a12b11cf3?q=80&w=2000&auto=format&fit=crop";
const keralaNature = "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?q=80&w=2000&auto=format&fit=crop";
const horseRideImg = "https://images.pexels.com/photos/1483030/pexels-photo-1483030.jpeg?auto=compress&cs=tinysrgb&w=2000";

const NatureFeature = ({ icon: Icon, title, description, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    viewport={{ once: true }}
    className="flex gap-3 sm:gap-4 p-6 sm:p-8 bg-white rounded-[2rem] sm:rounded-[2.5rem] border border-orange-50 shadow-[0_15px_35px_-10px_rgba(234,88,12,0.1)] hover:shadow-[0_25px_50px_-12px_rgba(234,88,12,0.15)] transition-all group"
  >
    <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-500 shadow-lg">
      <Icon size={24} />
    </div>
    <div className="space-y-2">
      <h3 className="text-lg font-serif font-bold text-slate-900">{title}</h3>
      <p className="text-slate-600 text-sm leading-relaxed font-medium">{description}</p>
    </div>
  </motion.div>
);

const OurDifference = () => {
  const natureHighlights = [
    { icon: Mountain, title: "The Himalayas", description: "Venture into the majestic peaks and serene valleys of the world's highest mountains." },
    { icon: Waves, title: "Kerala Backwaters", description: "Sail through the tranquil, palm-fringed waters of God's Own Country." },
    { icon: TreePine, title: "Lush Ghats", description: "Explore the emerald tea plantations and mist-covered hills of the Western Ghats." },
    { icon: Sun, title: "Thar Desert", description: "Experience the golden dunes and vibrant sunsets of Rajasthan's desert landscape." }
  ];

  return (
    <div className="relative min-h-screen bg-[#FFFBF5] overflow-hidden">
      <SEO 
        title="Our Edge & Difference" 
        description="Find out what makes Sunshine Travels unique. Discover our commitment to warm hospitality, certified guides, premium accommodation, and custom-tailored itineraries." 
        canonicalPath="/our-difference" 
      />
      {/* Pattern Background */}
      <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")' }}></div>

      <div className="relative z-10">
        {/* Cinematic Nature Hero */}
        <section className="relative h-[calc(70vh-2.5cm)] flex items-center pt-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={natureHero}
              alt="Himalayan Majesty"
              className="w-full h-full object-cover scale-105"
            />
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF5] via-transparent to-black/20"></div>
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="max-w-3xl space-y-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-[1px] bg-orange-500"></div>
                <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px]">Natural Heritage</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-serif font-bold text-white leading-snug tracking-wide">
                Our <span className="text-orange-500 italic text-[0.8em]">Edge.</span>
              </h1>
              <p className="text-white/90 text-lg md:text-xl font-medium max-w-xl leading-relaxed">
                From the snow-capped Himalayas to the tropical forests of the south, discover the diverse and breathtaking landscapes of Bharat.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Exploration Section */}
        <section className="py-16 md:py-32 container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="space-y-8 md:space-y-12">
              <div className="space-y-6">
                <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px] block">Endless Discovery</span>
                <h2 className="text-4xl md:text-7xl font-serif font-bold text-slate-900 leading-tight">
                  THE SOUL <br />
                  <span className="text-orange-600 italic">OF BHARAT.</span>
                </h2>
                <p className="text-slate-600 text-lg font-medium leading-relaxed max-w-lg">
                  India's nature is a tapestry of vibrant colors and profound peace. We curate journeys that take you deep into the heart of these natural wonders.
                </p>
              </div>

              <div className="space-y-8 md:space-y-10 relative">
                <div className="absolute left-[11px] top-2 bottom-2 w-[2px] bg-orange-100"></div>
                {[
                  { region: 'Northern Peaks', detail: 'Spiritual retreats in the shadows of the mighty Himalayas.' },
                  { region: 'Coastal Serenity', detail: 'Lush backwaters and pristine beaches of the Indian coastline.' },
                  { region: 'Jungle Majesty', detail: 'Encounter exotic wildlife in India\'s world-renowned reserves.' },
                  { region: 'Desert Gold', detail: 'Starlit nights and nomadic traditions in the golden Thar.' }
                ].map((item, idx) => (
                  <motion.div
                    key={item.region}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex gap-4 sm:gap-8 relative z-10"
                  >
                    <div className="w-6 h-6 rounded-full bg-white border-4 border-orange-500 shadow-xl shrink-0"></div>
                    <div className="space-y-1">
                      <span className="text-slate-900 font-serif font-bold text-xl">{item.region}</span>
                      <p className="text-slate-500 font-medium text-sm leading-relaxed">{item.detail}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-10 bg-orange-100/50 rounded-full blur-3xl -z-10 animate-pulse"></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {natureHighlights.map((highlight, index) => <NatureFeature key={index} {...highlight} index={index} />)}
                <div className="col-span-1 sm:col-span-2 relative mt-8 rounded-[2rem] sm:rounded-[3rem] overflow-hidden aspect-video shadow-2xl group border-4 sm:border-8 border-white">
                  <img src={keralaNature} alt="Kerala Serenity" className="w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent"></div>
                  <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 text-white">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <Compass className="text-orange-500" size={24} />
                      <span className="text-xs font-black uppercase tracking-[0.3em]">Discover Authenticity</span>
                    </div>
                    <p className="text-white/80 text-xs sm:text-sm font-medium max-w-xs italic">"Nature is the closest path to the divine."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Natural Stats */}
        <section className="py-16 md:py-24 bg-slate-950 text-white relative overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-20">
            <img src={natureHero} className="w-full h-full object-cover blur-sm" alt="Stats BG" />
          </div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-16 text-center">
              {[
                { label: 'National Parks', value: '100+', sub: 'Nature Reserves' },
                { label: 'Species Found', value: '45K+', sub: 'Unique Flora/Fauna' },
                { label: 'Mountain Peaks', value: '50+', sub: 'Above 7000m' },
                { label: 'Coastline', value: '7.5K', sub: 'Kilometers' }
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="space-y-4"
                >
                  <div className="text-4xl md:text-7xl font-serif font-bold text-orange-500">{stat.value}</div>
                  <div className="space-y-1">
                    <div className="text-[10px] font-black uppercase tracking-[0.4em] text-white">{stat.label}</div>
                    <div className="text-[8px] font-bold text-orange-500/50 uppercase tracking-widest">{stat.sub}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-32 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto relative group">
            <div className="absolute -inset-4 bg-orange-600/20 rounded-[2.5rem] sm:rounded-[4rem] blur-2xl group-hover:bg-orange-600/30 transition-all duration-500"></div>
            <div className="relative bg-slate-950 rounded-[2rem] sm:rounded-[3.5rem] p-8 sm:p-16 md:p-24 text-center text-white overflow-hidden shadow-2xl">
              <div className="absolute inset-0 opacity-40">
                <img src={horseRideImg} className="w-full h-full object-cover" alt="Horse Ride Background" />
              </div>
              <div className="relative z-10 space-y-6 sm:space-y-10">
                <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif font-bold italic">Breathe in the Wilderness</h2>
                <p className="text-white/90 text-base sm:text-lg md:text-xl font-medium max-w-xl mx-auto leading-relaxed">
                  Join us for an unforgettable expedition into the natural heart of the Indian subcontinent.
                </p>
                <div className="flex justify-center">
                  <Link to="/contact" className="bg-white text-orange-600 px-8 py-4 sm:px-12 sm:py-5 rounded-full font-black uppercase text-xs tracking-[0.3em] hover:bg-slate-900 hover:text-white transition-all shadow-2xl">
                    Plan Your Nature Escape
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OurDifference;
